import React from "react";

import {
  HashRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import {
  ClientProvider,
  useClient,
} from "./context/ClientContext";

/* =========================================================
   APPLICATION LAYOUTS
========================================================= */

import FirmLayout from "./layouts/FirmLayout";
import ClientLayout from "./layouts/ClientLayout";

/* =========================================================
   SHARED SCREENS
========================================================= */

import AuthenticationLoadingScreen from "./components/common/AuthenticationLoadingScreen";
import NoWorkspaceScreen from "./pages/NoWorkspace";
import usePublicPageScroll from "./hooks/usePublicPageScroll";

/* =========================================================
   AUTHENTICATION
========================================================= */

import Login from "./pages/Login";
import PasswordRecovery from "./pages/PasswordRecovery";
import Onboarding from "./pages/Onboarding";
import WorkspaceSetup from "./pages/WorkspaceSetup";
import ClientRegister from "./pages/ClientRegister";

import AcceptInvitation, {
  INVITATION_STORAGE_KEY,
} from "./pages/AcceptInvitation";

/* =========================================================
   CLIENT PORTAL PAGES
========================================================= */

import ClientDashboard from "./pages/client/ClientDashboard";
import ClientAI from "./pages/client/ClientAI";
import ClientTransactions from "./pages/client/ClientTransactions";
import ClientInvoices from "./pages/client/ClientInvoices";
import ClientBills from "./pages/client/ClientBills";
import ClientReports from "./pages/client/ClientReports";
import ClientDocuments from "./pages/client/ClientDocuments";
import ClientMyAccountant from "./pages/client/ClientMyAccountant";
import ClientSettings from "./pages/client/ClientSettings";
import ClientBanking from "./pages/client/ClientBanking";

import "./styles/profile.css";

/* =========================================================
   INVITATION HELPERS
========================================================= */

const isValidInvitationToken = (value) =>
  typeof value === "string" &&
  /^[0-9a-f]{64}$/i.test(value);

function getPendingInvitation() {
  try {
    const token = sessionStorage.getItem(
      INVITATION_STORAGE_KEY
    );

    return isValidInvitationToken(token)
      ? token
      : null;
  } catch {
    return null;
  }
}

/* =========================================================
   MAIN APPLICATION ROUTING

   App.jsx handles:
   - Authentication
   - Invitation routing
   - Verified workspace selection
   - Firm and client route separation

   All application UI belongs in layouts,
   components, and pages.

   Supabase RLS and Edge Functions enforce
   server-side organization authorization.
========================================================= */

function AppContent() {
  const {
    isAuthenticated,
    isLoading,
    isPasswordRecovery,
    availableWorkspaces,
    activeWorkspace,
    workspaceType,
    workspaceError,
  } = useClient();

  const location = useLocation();

  usePublicPageScroll(
    location.pathname,
    isPasswordRecovery
  );

  const isInvitationPage =
    location.pathname === "/accept-invitation";

  const isClientRegistrationPage =
    location.pathname === "/client-register";

  const isSetupPage =
    location.pathname === "/onboarding" ||
    location.pathname === "/workspace-setup";

  const pendingToken = getPendingInvitation();

  /* -------------------------------------------------------
     1. AUTHENTICATION LOADING
  ------------------------------------------------------- */

  if (isLoading) {
    return <AuthenticationLoadingScreen />;
  }

  /* -------------------------------------------------------
     2. PASSWORD RECOVERY
  ------------------------------------------------------- */

  if (isPasswordRecovery) {
    return <PasswordRecovery />;
  }

  /* -------------------------------------------------------
     3. PUBLIC INVITATION
  ------------------------------------------------------- */

  if (isInvitationPage) {
    return <AcceptInvitation />;
  }

  /* -------------------------------------------------------
     4. CLIENT REGISTRATION
  ------------------------------------------------------- */

  if (isClientRegistrationPage) {
    return isAuthenticated ? (
      <Navigate
        to="/accept-invitation"
        replace
      />
    ) : (
      <ClientRegister />
    );
  }

  /* -------------------------------------------------------
     5. UNAUTHENTICATED USER
  ------------------------------------------------------- */

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route
          path="/login"
          element={<Login />}
        />

        <Route
          path="*"
          element={
            <Navigate
              to="/login"
              replace
            />
          }
        />
      </Routes>
    );
  }

  /* -------------------------------------------------------
     6. PENDING INVITATION
  ------------------------------------------------------- */

  if (pendingToken) {
    return (
      <Navigate
        to={`/accept-invitation?token=${encodeURIComponent(
          pendingToken
        )}`}
        replace
      />
    );
  }

  /* -------------------------------------------------------
     7. WORKSPACE ERROR
  ------------------------------------------------------- */

  if (workspaceError) {
    return <NoWorkspaceScreen />;
  }

  /* -------------------------------------------------------
     8. NO VERIFIED WORKSPACE
  ------------------------------------------------------- */

  if (
    !Array.isArray(availableWorkspaces) ||
    availableWorkspaces.length === 0 ||
    !activeWorkspace?.id ||
    !workspaceType
  ) {
    return <NoWorkspaceScreen />;
  }

  /* -------------------------------------------------------
     9. FIRM / ACCOUNTANT WORKSPACE
  ------------------------------------------------------- */

  if (workspaceType === "firm") {
    if (isSetupPage) {
      return (
        <Routes>
          <Route
            path="/onboarding"
            element={<Onboarding />}
          />

          <Route
            path="/workspace-setup"
            element={<WorkspaceSetup />}
          />
        </Routes>
      );
    }

    // A firm workspace cannot render the client portal.
    if (
      location.pathname === "/client" ||
      location.pathname.startsWith("/client/")
    ) {
      return <Navigate to="/" replace />;
    }

    if (location.pathname === "/login") {
      return <Navigate to="/" replace />;
    }

    return <FirmLayout />;
  }

  /* -------------------------------------------------------
     10. CLIENT WORKSPACE
  ------------------------------------------------------- */

  if (workspaceType === "client") {
    return (
      <Routes>
        <Route
          path="/client"
          element={<ClientLayout />}
        >
          <Route
            index
            element={<ClientDashboard />}
          />

          <Route
            path="ai"
            element={<ClientAI />}
          />

          <Route
            path="transactions"
            element={<ClientTransactions />}
          />

          <Route
            path="invoices"
            element={<ClientInvoices />}
          />

          <Route
            path="bills"
            element={<ClientBills />}
          />

          <Route
            path="reports"
            element={<ClientReports />}
          />

          <Route
            path="documents"
            element={<ClientDocuments />}
          />

          <Route
            path="my-accountant"
            element={<ClientMyAccountant />}
          />

          <Route
            path="settings"
            element={<ClientSettings />}
          />

          <Route
            path="banking"
            element={<ClientBanking />}
          />
        </Route>

        <Route
          path="*"
          element={
            <Navigate
              to="/client"
              replace
            />
          }
        />
      </Routes>
    );
  }

  /* -------------------------------------------------------
     11. UNKNOWN WORKSPACE
  ------------------------------------------------------- */

  return <NoWorkspaceScreen />;
}

/* =========================================================
   APPLICATION ROOT
========================================================= */

export default function App() {
  return (
    <ClientProvider>
      <HashRouter>
        <AppContent />
      </HashRouter>
    </ClientProvider>
  );
}